﻿import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { str_isEmpty } from '@Util/Utils';
import { Project, Projects_Table_Props } from '@Util/Types';

import Table_Head from '@Elements/Table/Table_Head';
import Table_Data from '@Elements/Table/Table_Data';
import Edit_Projects_Popup from '@Modules/Popups/Edit_Projects_Popup';
import { Icon_Edit } from '@Elements/ImageElements/Icons';
import { Start_Button, Stop_Button } from '@Elements/Buttons';

export default function Projects_Table(props: Projects_Table_Props): JSX.Element {
    const [t] = useTranslation();

    const [sort_direction, set_sort_direction] = useState<"Ascending" | "Descending">("Ascending");
    const [active_sorting_column, set_active_sorting_column] = useState<string>("name");
    const [edit_project_popup_active, set_edit_project_popup_active] = useState<boolean>(false);
    const [edit_project_id, set_edit_project_id] = useState<number>(0);

    const table_data: Project[] = useMemo(() => {
        let sorted_data: Project[] = [];

        if (sort_direction == "Ascending") {
            sorted_data = props.projects.sort((a: Project, b: Project) => a[active_sorting_column] > b[active_sorting_column] ? 1 : -1);
        } else {
            sorted_data = props.projects.sort((a: Project, b: Project) => a[active_sorting_column] < b[active_sorting_column] ? 1 : -1);
        }

        sorted_data = sorted_data.sort((a: Project, b: Project) => a.isArchived === b.isArchived ? 0 : a.isArchived ? 1 : -1);

        return sorted_data;
    }, [props.projects, sort_direction, active_sorting_column]);

    function handleSetSortingOnTableHead(col: string): void {
        if (str_isEmpty(col)) {
            throw new Error("Handle sorting on table: Column is empty");
        }

        if (col === active_sorting_column) {
            if (sort_direction == "Ascending") {
                return set_sort_direction("Descending");
            } else {
                return set_sort_direction("Ascending");
            }
        }

        set_active_sorting_column(col);
        set_sort_direction("Ascending");
    }

    function Empty_Table(): JSX.Element {
        return (
            <tr>
                <Table_Data align="left">
                    <span className="Table_Data_Customer_Name">{t('Text.NoProjectsToShow')}</span>
                </Table_Data>
                <Table_Data align="right">
                    <></>
                </Table_Data>
            </tr>
        )
    }

    function render_start_or_stop_button(project: Project): JSX.Element {
        if (!project.isArchived) {
            if (props.current_project?.projectId === project.id) {
                return (
                    <Stop_Button
                        project_name={project.name}
                    />
                )
            } else {
                return (
                    <Start_Button
                        project_name={project.name}
                        start_time_id={project.id}
                    />
                )
            }
        }

        return null
    }

    return (
        <>
            <div className="Table_Wrapper">
                <table className="Table">
                    <thead>
                        <tr>
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Project')}
                                sortingAction={() => handleSetSortingOnTableHead('name')}
                                sortingDataName="name"
                                style={{ width: "50%" }}
                                
                            />
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Status')}
                                sortingAction={() => handleSetSortingOnTableHead('isArchived')}
                                sortingDataName="isArchived"
                                style={{ width: "25%" }}
                                
                            />
                            <Table_Head
                                align="right"
                                text=""
                                style={{ width: "25%" }}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            table_data.isEmpty() ?
                                <Empty_Table /> :
                                table_data.map((project: Project, index: number) =>
                                    <tr
                                        key={`projects_table_row${index}`}
                                        className={project.isArchived ? "Archived" : ""}
                                    >
                                        <Table_Data
                                            align="left"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "50%" }}
                                        >
                                            {project.name}
                                        </Table_Data>
                                        <Table_Data
                                            align="left"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "25%" }}
                                        >
                                            {
                                                project.isArchived ?
                                                    t('Text.Archived') :
                                                    t('Text.Active')
                                            }
                                        </Table_Data>
                                        <Table_Data
                                            align="right"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "25%" }}
                                        >
                                            <div className="Table_Buttons_Row">
                                                <button
                                                    type="button"
                                                    className="Button_Icon"
                                                    onClick={() => {
                                                        set_edit_project_id(project.id);
                                                        set_edit_project_popup_active(true);
                                                    }}
                                                >
                                                    <Icon_Edit />
                                                </button>
                                                { render_start_or_stop_button(project) }
                                            </div>
                                        </Table_Data>
                                    </tr>
                                )
                        }
                    </tbody>
                </table>
            </div>
            <Edit_Projects_Popup
                open={edit_project_popup_active}
                set_open={set_edit_project_popup_active}
                chosen_projects_id={edit_project_id}
            />
        </>
    );
}